import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import { FaSave, FaSpinner, FaEdit, FaTrash, FaPlus, FaStar } from 'react-icons/fa';

const TestimonialManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    text: '',
    image: '',
    rating: 5,
    isApproved: true,
    isPublished: true,
    order: 0,
    role: ''
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Authentication token not found. Please login again.');
        return;
      }

      console.log('Fetching testimonials from:', `${API_URL}/content/public/testimonials`);
      const response = await axios.get(
        `${API_URL}/content/public/testimonials?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTestimonials(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });

      if (error.response?.status === 401) {
        toast.error('Unauthorized. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found. Check backend configuration.');
      } else if (!error.response) {
        toast.error('Cannot connect to backend server. Make sure it\'s running on port 5000.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load testimonials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedMimes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedMimes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        toast.error('Authentication token not found. Please login again.');
        return;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      console.log('Uploading image to:', `${API_URL}/content/admin/testimonials/upload/image`);
      const response = await axios.post(
        `${API_URL}/content/admin/testimonials/upload/image`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`
            // Note: Do NOT set Content-Type header for FormData
            // Let axios automatically set it with the correct boundary
          }
        }
      );

      if (response.data.success) {
        handleInputChange('image', response.data.data.url);
        toast.success('Image uploaded to Cloudinary successfully');
      }
    } catch (error) {
      console.error('Error uploading image:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });

      if (error.response?.status === 401) {
        toast.error('Unauthorized. Please login again.');
      } else if (error.response?.status === 413) {
        toast.error('File is too large. Maximum size is 5MB.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid file format.');
      } else if (!error.response) {
        toast.error('Cannot connect to backend server. Make sure it\'s running on port 5000.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to upload image');
      }
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.text) {
      toast.error('Name and testimonial text are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (editingId) {
        // Update
        const response = await axios.put(
          `${API_URL}/content/admin/testimonials/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Testimonial updated successfully');
          setEditingId(null);
        }
      } else {
        // Create
        const response = await axios.post(
          `${API_URL}/content/admin/testimonials`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Testimonial created successfully');
        }
      }

      resetForm();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error(error.response?.data?.message || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      text: testimonial.text,
      image: testimonial.image || '',
      rating: testimonial.rating || 5,
      isApproved: testimonial.isApproved || true,
      isPublished: testimonial.isPublished || true,
      order: testimonial.order || 0,
      role: testimonial.role || ''
    });
    setEditingId(testimonial._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/content/admin/testimonials/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Testimonial deleted successfully');
        fetchTestimonials();
      }
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      text: '',
      image: '',
      rating: 5,
      isApproved: true,
      isPublished: true,
      order: 0,
      role: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const filteredTestimonials = testimonials.filter((testimonial) =>
    testimonial.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    testimonial.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && !showForm) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <FaSpinner className="animate-spin text-3xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Testimonial Management</h1>
        <p className="text-gray-600">Manage customer testimonials displayed on the website</p>
      </div>

      {!showForm ? (
        <>
          {/* Search and Add Button */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex gap-4 flex-col md:flex-row">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name or text..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap"
              >
                <FaPlus /> Add Testimonial
              </button>
            </div>
          </div>

          {/* Testimonials List */}
          <div className="space-y-4">
            {filteredTestimonials.length === 0 ? (
              <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500">
                No testimonials found
              </div>
            ) : (
              filteredTestimonials.map((testimonial) => (
                <div key={testimonial._id} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex gap-6">
                    {/* Image */}
                    {testimonial.image && (
                      <div className="flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">{testimonial.name}</h3>
                          {testimonial.role && (
                            <p className="text-xs text-gray-500 mt-1">{testimonial.role}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1 mb-2">
                            {[...Array(testimonial.rating || 5)].map((_, i) => (
                              <FaStar key={i} className="text-yellow-500 text-sm" />
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(testimonial)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(testimonial._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{testimonial.text}</p>

                      <div className="flex gap-4 text-xs">
                        <span className={`px-2 py-1 rounded ${testimonial.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {testimonial.isPublished ? 'Published' : 'Draft'}
                        </span>
                        <span className={`px-2 py-1 rounded ${testimonial.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {testimonial.isApproved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        /* Form */
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {editingId ? 'Edit Testimonial' : 'Create New Testimonial'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter customer name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Image</label>
              <div className="space-y-3">
                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition">
                  <label className="flex flex-col items-center justify-center cursor-pointer">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">Upload Image to Cloudinary</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF, WebP (max 5MB)</p>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                    {uploading && <FaSpinner className="animate-spin text-blue-600 mt-2" />}
                    {!uploading && <span className="text-blue-600 text-sm mt-2">Click to upload</span>}
                  </label>
                </div>

                {/* URL Input */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Or paste image URL</label>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => handleInputChange('image', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Image Preview */}
                {formData.image && (
                  <div className="flex items-center gap-4">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5FcnJvcjwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleInputChange('image', '')}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove Image
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonial Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Testimonial Text *</label>
              <textarea
                value={formData.text}
                onChange={(e) => handleInputChange('text', e.target.value)}
                placeholder="Enter testimonial text..."
                rows="6"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role/Designation</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                placeholder="e.g. CEO, Customer, Driver..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating (1-5 stars)</label>
              <select
                value={formData.rating}
                onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>
                    {num} Star{num !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value))}
                placeholder="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>

            {/* Publish Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Published</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isApproved}
                    onChange={(e) => handleInputChange('isApproved', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Approved</span>
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {saving ? 'Saving...' : 'Save Testimonial'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TestimonialManagement;
